import React from 'react';
import styled from 'styled-components';
import type { LucideIcon } from 'lucide-react';

export type DerivativeTone = 'rates' | 'trs' | 'currency' | 'credit' | 'swaption' | 'futures' | 'options';

export interface DerivativeSignal {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
}

const tones: Record<DerivativeTone, {
  primary: string;
  secondary: string;
  soft: string;
  border: string;
  glow: string;
}> = {
  rates: {
    primary: '#38bdf8',
    secondary: '#7dd3fc',
    soft: 'rgba(56, 189, 248, 0.12)',
    border: 'rgba(56, 189, 248, 0.34)',
    glow: 'rgba(14, 165, 233, 0.2)',
  },
  trs: {
    primary: '#22c55e',
    secondary: '#86efac',
    soft: 'rgba(34, 197, 94, 0.12)',
    border: 'rgba(34, 197, 94, 0.34)',
    glow: 'rgba(34, 197, 94, 0.18)',
  },
  currency: {
    primary: '#06b6d4',
    secondary: '#67e8f9',
    soft: 'rgba(6, 182, 212, 0.12)',
    border: 'rgba(6, 182, 212, 0.34)',
    glow: 'rgba(6, 182, 212, 0.18)',
  },
  credit: {
    primary: '#f59e0b',
    secondary: '#fbbf24',
    soft: 'rgba(245, 158, 11, 0.12)',
    border: 'rgba(245, 158, 11, 0.34)',
    glow: 'rgba(217, 119, 6, 0.18)',
  },
  swaption: {
    primary: '#a78bfa',
    secondary: '#c4b5fd',
    soft: 'rgba(167, 139, 250, 0.13)',
    border: 'rgba(167, 139, 250, 0.34)',
    glow: 'rgba(139, 92, 246, 0.2)',
  },
  futures: {
    primary: '#fb7185',
    secondary: '#fda4af',
    soft: 'rgba(251, 113, 133, 0.12)',
    border: 'rgba(251, 113, 133, 0.34)',
    glow: 'rgba(244, 63, 94, 0.18)',
  },
  options: {
    primary: '#f472b6',
    secondary: '#f9a8d4',
    soft: 'rgba(244, 114, 182, 0.12)',
    border: 'rgba(244, 114, 182, 0.34)',
    glow: 'rgba(219, 39, 119, 0.18)',
  },
};

const tone = (name: DerivativeTone) => tones[name];

export const DerivativeHero = styled.section<{ $tone: DerivativeTone }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 0.74fr);
  gap: 18px;
  align-items: stretch;
  padding: 18px;
  border-radius: 14px;
  border: 1px solid ${({ $tone }) => tone($tone).border};
  background:
    radial-gradient(circle at 10% 0%, ${({ $tone }) => tone($tone).glow} 0%, rgba(15, 23, 42, 0) 36%),
    linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(2, 6, 23, 0.96) 100%);
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.24);
  margin-bottom: 1.25rem;

  @media (max-width: 1040px) {
    grid-template-columns: 1fr;
  }
`;

const HeroMain = styled.div`
  display: flex;
  gap: 14px;
  min-width: 0;
`;

const HeroIcon = styled.div<{ $tone: DerivativeTone }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ $tone }) => tone($tone).secondary};
  background: ${({ $tone }) => tone($tone).soft};
  border: 1px solid ${({ $tone }) => tone($tone).border};
`;

const HeroText = styled.div`
  min-width: 0;
`;

const HeroEyebrow = styled.div<{ $tone: DerivativeTone }>`
  color: ${({ $tone }) => tone($tone).secondary};
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

const HeroTitle = styled.h2`
  margin: 0;
  color: #f8fafc;
  font-size: 22px;
  line-height: 1.18;
  letter-spacing: 0;
`;

const HeroDescription = styled.p`
  margin: 8px 0 0;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.5;
  max-width: 760px;
`;

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 13px;
`;

const ScopePill = styled.div<{ $tone: DerivativeTone }>`
  min-height: 28px;
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid ${({ $tone }) => tone($tone).border};
  background: rgba(15, 23, 42, 0.58);
  color: #cbd5e1;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const HeroSignalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const HeroSignal = styled.div<{ $tone: DerivativeTone; $accent?: boolean }>`
  min-width: 0;
  padding: 13px;
  border-radius: 12px;
  border: 1px solid ${({ $tone, $accent }) => $accent ? tone($tone).border : 'rgba(71, 85, 105, 0.38)'};
  background: ${({ $tone, $accent }) => $accent ? tone($tone).soft : 'rgba(15, 23, 42, 0.68)'};
`;

const SignalLabel = styled.div`
  color: #64748b;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

const SignalValue = styled.div<{ $tone: DerivativeTone; $accent?: boolean }>`
  color: ${({ $tone, $accent }) => $accent ? tone($tone).secondary : '#f8fafc'};
  font-size: 16px;
  font-weight: 900;
  line-height: 1.25;
  overflow-wrap: anywhere;
`;

export const DerivativePreviewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 2px 0 14px;
`;

export const DerivativePreviewButton = styled.button<{ $tone: DerivativeTone }>`
  min-height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid ${({ $tone }) => tone($tone).border};
  background: ${({ $tone }) => tone($tone).soft};
  color: ${({ $tone }) => tone($tone).secondary};
  cursor: pointer;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.02em;
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;

  &:hover:not(:disabled) {
    border-color: ${({ $tone }) => tone($tone).secondary};
    background: rgba(15, 23, 42, 0.72);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.58;
    cursor: not-allowed;
  }
`;

export const DerivativeMetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 1.25rem;
`;

export const DerivativeMetricTile = styled.div<{ $tone: DerivativeTone }>`
  min-width: 0;
  padding: 12px 13px;
  border-radius: 11px;
  border: 1px solid rgba(71, 85, 105, 0.4);
  background:
    radial-gradient(circle at 100% 0%, ${({ $tone }) => tone($tone).glow} 0%, rgba(15, 23, 42, 0) 42%),
    rgba(15, 23, 42, 0.68);
`;

export const DerivativeMetricLabel = styled.div`
  color: #64748b;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

export const DerivativeMetricValue = styled.div<{ $tone: DerivativeTone }>`
  color: ${({ $tone }) => tone($tone).secondary};
  font-size: 16px;
  font-weight: 900;
  line-height: 1.24;
  overflow-wrap: anywhere;
`;

export const DerivativeReferenceHint = styled.div<{ $tone: DerivativeTone }>`
  color: ${({ $tone }) => tone($tone).secondary};
  font-size: 11px;
  font-weight: 700;
  margin-top: 5px;
`;

export const formatDerivativeCurrency = (value: number, currency = 'USD') => {
  if (!Number.isFinite(value) || value <= 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatDerivativeNumber = (value: number, maximumFractionDigits = 2) => {
  if (!Number.isFinite(value) || value <= 0) return '-';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits }).format(value);
};

export const DerivativeHeroPanel: React.FC<{
  toneName: DerivativeTone;
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  description: string;
  badges: string[];
  signals: DerivativeSignal[];
}> = ({ toneName, icon: Icon, eyebrow, title, description, badges, signals }) => (
  <DerivativeHero $tone={toneName}>
    <HeroMain>
      <HeroIcon $tone={toneName}><Icon size={22} /></HeroIcon>
      <HeroText>
        <HeroEyebrow $tone={toneName}>{eyebrow}</HeroEyebrow>
        <HeroTitle>{title}</HeroTitle>
        <HeroDescription>{description}</HeroDescription>
        <PillRow>
          {badges.map((badge) => (
            <ScopePill key={badge} $tone={toneName}>{badge}</ScopePill>
          ))}
        </PillRow>
      </HeroText>
    </HeroMain>
    <HeroSignalGrid>
      {signals.map((signal) => (
        <HeroSignal key={signal.label} $tone={toneName} $accent={signal.accent}>
          <SignalLabel>{signal.label}</SignalLabel>
          <SignalValue $tone={toneName} $accent={signal.accent}>{signal.value}</SignalValue>
        </HeroSignal>
      ))}
    </HeroSignalGrid>
  </DerivativeHero>
);
